import { z } from 'zod';

/**
 * Task #234 — StatCan Canadian Index of Multiple Deprivation (CIMD) 2021.
 *
 * Communities carry the government's own vulnerability data: the four CIMD
 * dimension quintiles, resolved at the dissemination-area (DA) level by a
 * point-intersect query of the community centroid against the official
 * GOC-hosted ArcGIS service (open.canada.ca dataset
 * ec6dc8e7-2fa0-4e49-8969-38541ca0a34d). Stored on `communities`
 * (migration 0068); this module is the pure contract — dimension vocabulary,
 * quintile semantics, the REST query URL builder, and the response parser.
 * No fetch here: apps own their I/O.
 *
 * SEMANTICS (CIMD 2021 User Guide, StatCan 45-20-0001 2023002):
 * - Four dimensions, each its own index: Residential Instability, Economic
 *   Dependency, Ethno-cultural Composition, Situational Vulnerability.
 * - Within a dimension, DAs are quintiled: 1 = least deprived … 5 = most
 *   deprived. Quintiles are NATIONAL reference bands (provinces-only file).
 * - StatCan publishes NO official composite. We present only the four
 *   published quintiles — no invented roll-up.
 * - 409 DAs are suppressed (population under 40) and 318 excluded (≥50%
 *   non-response); 55,726 DAs carry scores. An unresolved/suppressed DA is
 *   an honest `null` — never fabricated.
 *
 * HEADLINE DIMENSION: Situational Vulnerability. The User Guide singles it
 * out as the emergency-management-relevant dimension (socio-demographic
 * conditions in housing + education; its indicators include Indigenous
 * identification, housing condition, education attainment, family structure)
 * — exactly the operator question "which communities are least able to
 * absorb a hazard". The other three render alongside it in the panel; only
 * the headline chip / briefing line / map shading key on it.
 */

/* ------------------------------------------------------------------ *
 * Dimensions + quintiles
 * ------------------------------------------------------------------ */

export const CIMD_DIMENSION_KEYS = [
  'residential_instability',
  'economic_dependency',
  'ethno_cultural_composition',
  'situational_vulnerability',
] as const;
export type CimdDimensionKey = (typeof CIMD_DIMENSION_KEYS)[number];

/** StatCan's own dimension names (User Guide) — render these verbatim. */
export const CIMD_DIMENSION_LABEL: Record<CimdDimensionKey, string> = {
  residential_instability: 'Residential Instability',
  economic_dependency: 'Economic Dependency',
  ethno_cultural_composition: 'Ethno-cultural Composition',
  situational_vulnerability: 'Situational Vulnerability',
};

/** The dimension the headline chip / briefing line / map shading key on. */
export const CIMD_HEADLINE_DIMENSION: CimdDimensionKey = 'situational_vulnerability';

/** Provenance string surfaces pair with the data wherever it renders. */
export const CIMD_VERSION = 'CIMD 2021';
export const CIMD_PROVENANCE_LABEL = 'CIMD 2021 (StatCan)';

export const CimdQuintileSchema = z.number().int().min(1).max(5);
export type CimdQuintile = 1 | 2 | 3 | 4 | 5;

/** "Q3 of 5" — the bare quintile display form. */
export function formatCimdQuintile(q: CimdQuintile): string {
  return `Q${q} of 5`;
}

/** StatCan defines the endpoints; only assert what they publish. */
export function cimdQuintileDescriptor(q: CimdQuintile): string | null {
  if (q === 1) return 'least deprived';
  if (q === 5) return 'most deprived';
  return null;
}

/* ------------------------------------------------------------------ *
 * Community-carried fields (responses)
 * ------------------------------------------------------------------ */

const NullableQuintile = CimdQuintileSchema.nullable();

/** The CIMD block a community row carries (migration 0068). All-null with a
 *  null `cimd_enriched_at` = never enriched; quintiles null with enriched_at
 *  SET = queried but honestly unresolved (suppressed/unscored DA). */
export const CommunityCimdSchema = z.object({
  cimd_residential_instability_q: NullableQuintile,
  cimd_economic_dependency_q: NullableQuintile,
  cimd_ethno_cultural_composition_q: NullableQuintile,
  cimd_situational_vulnerability_q: NullableQuintile,
  cimd_dauid: z.string().nullable(),
  cimd_version: z.string().nullable(),
  cimd_enriched_at: z.string().nullable(),
});
export type CommunityCimd = z.infer<typeof CommunityCimdSchema>;

/** Pull the headline (Situational Vulnerability) quintile off a community-ish
 *  object; null = honest unknown (not enriched, or unresolved DA). */
export function cimdHeadlineQuintile(c: {
  cimd_situational_vulnerability_q: number | null;
}): CimdQuintile | null {
  const q = c.cimd_situational_vulnerability_q;
  return q !== null && Number.isInteger(q) && q >= 1 && q <= 5 ? (q as CimdQuintile) : null;
}

/* ------------------------------------------------------------------ *
 * The official service — query URL + response parser (pure)
 * ------------------------------------------------------------------ */

/** Official GOC/StatCan CIMD 2021 MapServer (English). Every sublayer carries
 *  all four *_Q fields + DAUID; layer 3 is Situational Vulnerability (the
 *  headline) but the choice is cosmetic for a point query. */
export const CIMD_SERVICE_URL =
  'https://maps-cartes.services.geo.ca/server2_serveur2/rest/services/StatCan/multiple_deprivation_2021/MapServer/3';

/** Point-intersect query: which scored DA contains this centroid, and its four
 *  quintiles. Geometry only — no geometry returned, one feature expected. */
export function buildCimdPointQueryUrl(lon: number, lat: number): string {
  const params = new URLSearchParams({
    geometry: `${lon},${lat}`,
    geometryType: 'esriGeometryPoint',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: [
      'DAUID',
      'Residential_instability_Q',
      'Economic_dependency_Q',
      'Ethno_cultural_composition_Q',
      'Situational_vulnerability_Q',
    ].join(','),
    returnGeometry: 'false',
    f: 'json',
  });
  return `${CIMD_SERVICE_URL}/query?${params.toString()}`;
}

/** A parsed point-query outcome. `no_da` = the service answered and no scored
 *  DA contains the point (suppressed/excluded DA, water, or outside coverage)
 *  — an honest unknown, distinct from a transport/service error (thrown). */
export type CimdPointQueryResult =
  | {
      status: 'resolved';
      dauid: string | null;
      quintiles: Record<CimdDimensionKey, CimdQuintile | null>;
    }
  | { status: 'no_da' };

/** The service ships quintiles as doubles; admit only StatCan's 1..5 integers,
 *  anything else (null, suppressed blanks, garbage) → honest null. */
function toQuintile(v: unknown): CimdQuintile | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 5
    ? (v as CimdQuintile)
    : null;
}

/**
 * Parse an ArcGIS point-query response. Throws on a malformed body or an
 * ArcGIS-level error payload (the caller treats that as service-down and
 * leaves the community un-enriched); returns `no_da` on an empty feature set.
 * A boundary-straddling point can intersect 2 DAs — first feature wins.
 */
export function parseCimdPointQueryResponse(body: unknown): CimdPointQueryResult {
  if (typeof body !== 'object' || body === null) {
    throw new Error('CIMD query: non-object response body');
  }
  const obj = body as Record<string, unknown>;
  if (obj.error !== undefined) {
    const err = obj.error as { message?: unknown } | null;
    throw new Error(
      `CIMD query: service error — ${typeof err?.message === 'string' ? err.message : 'unknown'}`,
    );
  }
  if (!Array.isArray(obj.features)) {
    throw new Error('CIMD query: response has no features array');
  }
  if (obj.features.length === 0) return { status: 'no_da' };
  const first = obj.features[0] as { attributes?: Record<string, unknown> };
  const attrs = first?.attributes;
  if (typeof attrs !== 'object' || attrs === null) {
    throw new Error('CIMD query: feature has no attributes');
  }
  return {
    status: 'resolved',
    dauid: typeof attrs.DAUID === 'string' && attrs.DAUID.length > 0 ? attrs.DAUID : null,
    quintiles: {
      residential_instability: toQuintile(attrs.Residential_instability_Q),
      economic_dependency: toQuintile(attrs.Economic_dependency_Q),
      ethno_cultural_composition: toQuintile(attrs.Ethno_cultural_composition_Q),
      situational_vulnerability: toQuintile(attrs.Situational_vulnerability_Q),
    },
  };
}
